<template>
  <b-row :class="`margin-0 ${rowClass}`">
    <b-col v-if="title" cols="12" md="12" class="padding-x-0">
      <div class="font-size-12 color-complementary-300 margin-bottom-8">
        {{ title }}
        <span v-if="infoTooltip" v-b-tooltip.hover :title="infoTooltip">
          <mdicon name="help-circle-outline" size="16" />
        </span>
      </div>
    </b-col>
    <b-col cols="12" md="12" class="position-relative padding-x-0 input">
      <div class="position-relative" v-click-outside="hidePasswordChecker">
        <slide-y-down-transition>
          <div
            class="input__check-password"
            v-if="
              type === 'password' &&
              passwordCheckerModel &&
              value &&
              isPasswordChecker
            "
          >
            <div class="input__strong-label">
              {{ analyzePasswordData.label }}
            </div>
            <div class="input__progress-box">
              <span
                :class="`input__progress background-${analyzePasswordData.variant} width-percent-${analyzePasswordData.width}`"
              ></span>
            </div>
            <div class="input__strong-information">
              {{ $lang.password_number_word }}
            </div>
          </div>
        </slide-y-down-transition>
        <span v-if="rightIcon" class="input__right-icon">
          <mdicon :name="rightIcon" size="24" />
        </span>
        <span
          v-if="hasEraseIcon || loading"
          class="input__erase-icon flex"
          @click="erase"
        >
          <mdicon v-if="loading" name="loading" size="20" class="spinner" />
          <mdicon v-else name="close" size="24" />
        </span>
        <b-form-textarea
          v-if="type === 'textarea'"
          v-model="inputValue"
          :id="id"
          :rows="textareaRows"
          :autocomplete="autocomplete"
          :autofocus="autoFocus"
          :disabled="disabled"
          :placeholder="placeholder"
          :state="generateState"
          :aria-describedby="`${id}-help ${id}-feedback`"
          :trim="hasTrim"
          @focus="focus"
          @blur="blur"
        />
        <b-form-input
          v-else
          v-model="inputValue"
          :type="type"
          :placeholder="placeholder"
          :id="id"
          :minLength="minLength"
          :maxLength="maxLength"
          :autocomplete="autocomplete"
          :autofocus="autoFocus"
          :disabled="disabled"
          :state="generateState"
          :aria-describedby="`${id}-help ${id}-feedback`"
          :trim="hasTrim"
          :class="{
            'left-icon': hasLeftIcon || isPasswordTogglerIcon,
            'right-icon': rightIcon,
            'erase-icon': hasEraseIcon,
          }"
          @input="checkPassword"
          @focus="focus"
          @blur="blur"
        />
        <span
          v-if="type === 'password' && isPasswordTogglerIcon"
          class="input__password-toggler"
          @click="passwordToggler()"
        >
          <mdicon :name="passwordTogglerIcon" size="24" />
        </span>
        <span
          v-if="shouldShowCharCounter && inputValue"
          class="font-size-12 color-complementary-300 position-absolute left-0 bottom--15"
        >
          {{ inputValue.length }} / {{ maxLength }}
        </span>
      </div>
    </b-col>
    <b-col cols="12" md="12" class="padding-x-0">
      <FeedbackMessages
        :state="generateState"
        :label="label"
        :vuelidateObj="vuelidateObj"
      />
    </b-col>
  </b-row>
</template>

<script>
import FeedbackMessages from "../feedback-messages/FeedbackMessages.vue";
import { includes } from "ramda";

export default {
  components: { FeedbackMessages },
  props: {
    value: {}, // watch model
    name: {
      type: String,
      default: null,
    },
    title: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "عنوان",
    },
    id: {
      type: String,
      default: "default-id",
    },
    type: {
      type: String,
      default: "text",
    },
    textareaRows: {
      type: Number,
      default: 3,
    },
    placeholder: {
      type: String,
      default: "",
    },
    autocomplete: {
      type: String,
      default: "off",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    vuelidateObj: {
      type: Object,
      default: null,
    },
    minLength: {
      type: [String, Number],
      default: 1,
    },
    maxLength: {
      type: [String, Number],
      default: 256,
    },
    hasLeftIcon: {
      type: Boolean,
      default: false,
    },
    isPasswordChecker: {
      type: Boolean,
      default: false,
    },
    rowClass: {
      type: String,
      default: "margin-bottom-22",
    },
    rightIcon: {
      type: String,
      default: null,
    },
    autoFocus: {
      type: Boolean,
      default: false,
    },
    hasEraseIcon: {
      type: Boolean,
      default: false,
    },
    infoTooltip: {
      type: String,
      default: "",
    },
    isPasswordTogglerIcon: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    hasTrim: {
      type: Boolean,
      default: false,
    },
    shouldShowCharCounter: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      inputFontSize: "font_18",
      passwordTogglerIcon: "eye-off-outline",
      passwordCheckerModel: false,
      analyzePasswordData: {
        label: "رمزعبور ضعیف",
        width: "35",
        variant: "error",
      },
    };
  },
  computed: {
    inputValue: {
      /* guidance: watching input value */
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", this.name, val);
      },
    },
    generateState() {
      return this.vuelidateObj && this.vuelidateObj.$dirty
        ? !this.vuelidateObj.$error
        : null;
    },
  },
  methods: {
    includes,
    passwordToggler() {
      /* guidance: toggle change type of password input (when clicked eye icon) */
      if (includes(this.type, ["text", "password"])) {
        const el = document.getElementById(this.id);
        document.getElementById(this.id).type =
          el.type === "password" ? "text" : "password";
        this.inputFontSize = el.type === "password" ? "font_25" : "font_18";
        this.passwordTogglerIcon =
          el.type === "password" ? "eye-off-outline" : "eye-outline";
      }
    },
    checkPassword(password) {
      if (!password) {
        this.passwordCheckerModel = false;
        return;
      }
      const strongRegex = new RegExp(
        "^(?=.*[a-zA-Z])(?=.*\\d)(?=.*\\W).{8,}",
        "g"
      );
      const mediumRegex = new RegExp("^(?=.*[a-zA-Z])(?=.*\\d).{8,}", "g");
      const onlyCharRegex = new RegExp("^(?=.*[a-zA-Z]).{8,}", "g");
      if (
        strongRegex.test(password) ||
        (mediumRegex.test(password) && password.length > 10)
      ) {
        this.analyzePasswordData = {
          label: "رمزعبور قوی",
          width: "100",
          variant: "success",
        };
        this.passwordCheckerModel = true;
      } else if (
        mediumRegex.test(password) ||
        (onlyCharRegex.test(password) && password.length > 10)
      ) {
        this.analyzePasswordData = {
          label: "رمزعبور متوسط",
          width: "65",
          variant: "warning",
        };
        this.passwordCheckerModel = true;
      } else {
        this.analyzePasswordData = {
          label: "رمزعبور ضعیف",
          width: "35",
          variant: "error",
        };
        this.passwordCheckerModel = true;
      }
    },
    showPasswordChecker() {
      this.passwordCheckerModel = true;
    },
    hidePasswordChecker() {
      this.passwordCheckerModel = false;
    },
    blur() {
      this.$emit("blur", this.name, this.inputValue);
    },
    focus() {
      this.$emit("focus", this.name);
      this.showPasswordChecker();
    },
    erase() {
      this.$emit("erase", this.name);
    },
  },
};
</script>
